import React, { useMemo, useState } from "react";
import {
  HiRefresh,
  HiOutlineArrowLeft,
  HiOutlineExclamationCircle,
  HiOutlineExclamation,
  HiOutlineCheckCircle,
  HiOutlineInformationCircle,
  HiOutlineSearch,
  HiOutlineX,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineClipboardCopy,
  HiOutlineCalendar,
} from "react-icons/hi";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";

import API from "../../api";
import DateTimeInput from "../../components/DateTimeInput";

const PRESET_MS = { "1h": 36e5, "6h": 6 * 36e5, "24h": 24 * 36e5, "48h": 48 * 36e5, "7d": 7 * 24 * 36e5 };
const PRESETS = [
  { key: "1h", label: "1h" },
  { key: "6h", label: "6h" },
  { key: "24h", label: "24h" },
  { key: "48h", label: "48h" },
  { key: "7d", label: "7j" },
];

const parseLogMessage = (rawMessage) => {
  try {
    const parsed = JSON.parse(rawMessage);
    return {
      isParsed: true,
      level: parsed.level || "info",
      message: parsed.message || rawMessage,
      timestamp: parsed.timestamp,
      service: parsed.service,
      webAppUsername: parsed.webAppUsername,
      messageId: parsed.messageId,
      admissionId: parsed.admissionId,
      laboratoryCode: parsed.laboratoryCode,
      caller: parsed.caller,
      raw: parsed,
    };
  } catch {
    return { isParsed: false, level: "info", message: rawMessage, raw: rawMessage };
  }
};

const getLogLevel = (parsed) => {
  if (parsed.isParsed) {
    if (parsed.level === "error") return "error";
    if (parsed.level === "warn" || parsed.level === "warning") return "warn";
    if (parsed.level === "success") return "success";
    return "info";
  }
  const m = parsed.message.toLowerCase();
  if (m.includes("error") || m.includes("erreur") || m.includes("failed") || m.includes("exception")) return "error";
  if (m.includes("warn") || m.includes("warning") || m.includes("attention")) return "warn";
  if (m.includes("success") || m.includes("completed") || m.includes("terminé")) return "success";
  return "info";
};

const levelIcon = (level) => {
  if (level === "error") return <HiOutlineExclamationCircle className="h-4 w-4 text-red-500" />;
  if (level === "warn") return <HiOutlineExclamation className="h-4 w-4 text-orange-500" />;
  if (level === "success") return <HiOutlineCheckCircle className="h-4 w-4 text-emerald-500" />;
  return <HiOutlineInformationCircle className="h-4 w-4 text-gray-400" />;
};

const levelAccent = (level) => {
  if (level === "error") return "border-l-red-400";
  if (level === "warn") return "border-l-orange-300";
  return "border-l-transparent";
};

const formatDateForInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const formatTimestamp = (timestamp) =>
  new Date(timestamp).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const MachineLogs = ({ user }) => {
  const history = useHistory();
  const { streamName } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const logGroupName = searchParams.get("logGroupName");

  const [search, setSearch] = useState("");
  const [admissionIdFilter, setAdmissionIdFilter] = useState(searchParams.get("admissionId") || "");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [expandedKeys, setExpandedKeys] = useState({});

  const urlStart = searchParams.get("startDate");
  const urlEnd = searchParams.get("endDate");
  const [preset, setPreset] = useState(urlStart ? null : "24h");
  const [customStart, setCustomStart] = useState(urlStart ? formatDateForInput(urlStart) : "");
  const [customEnd, setCustomEnd] = useState(urlEnd ? formatDateForInput(urlEnd) : "");
  const [showDatePicker, setShowDatePicker] = useState(false);

  const { data, isLoading, isFetching, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["machine-logs", streamName, logGroupName, preset, customStart, customEnd],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ logGroupName: logGroupName || "" });
      if (pageParam) {
        params.append("nextToken", pageParam);
      } else if (customStart) {
        params.append("startDate", new Date(customStart).toISOString());
        if (customEnd) params.append("endDate", new Date(customEnd).toISOString());
      } else {
        const ms = PRESET_MS[preset] ?? PRESET_MS["24h"];
        params.append("startDate", new Date(Date.now() - ms).toISOString());
      }

      const response = await API.get(`/machine/${encodeURIComponent(streamName)}/logs?${params.toString()}`);
      return response.data;
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
    enabled: !!streamName,
  });

  const logs = useMemo(() => {
    const events = (data?.pages ?? [])
      .flatMap((page) => page.events)
      .map((log, index) => ({
        ...log,
        key: `${new Date(log.timestamp).getTime()}-${new Date(log.ingestionTime).getTime()}-${index}`,
        parsed: parseLogMessage(log.message),
      }));
    events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return events;
  }, [data]);

  const counts = useMemo(
    () => ({
      all: logs.length,
      error: logs.filter((l) => getLogLevel(l.parsed) === "error").length,
      warn: logs.filter((l) => getLogLevel(l.parsed) === "warn").length,
      success: logs.filter((l) => getLogLevel(l.parsed) === "success").length,
      info: logs.filter((l) => getLogLevel(l.parsed) === "info").length,
    }),
    [logs],
  );

  const filteredLogs = useMemo(() => {
    let result = logs;
    if (admissionIdFilter) result = result.filter((l) => l.parsed.admissionId === admissionIdFilter);
    if (selectedLevel !== "all") result = result.filter((l) => getLogLevel(l.parsed) === selectedLevel);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((l) => {
        const p = l.parsed;
        return `${p.message} ${p.service || ""} ${p.webAppUsername || ""} ${p.admissionId || ""} ${p.messageId || ""}`.toLowerCase().includes(q);
      });
    }
    return result;
  }, [logs, admissionIdFilter, selectedLevel, search]);

  const onPreset = (key) => {
    setPreset(key);
    setCustomStart("");
    setCustomEnd("");
    setShowDatePicker(false);
  };

  const toggleExpand = (key) => setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(user.role === "admin" || user.role === "superadmin") && (
              <button
                type="button"
                onClick={() => history.push("/machine")}
                className="rounded-lg border border-transparent p-2 text-gray-600 transition-all hover:border-gray-200 hover:bg-white hover:text-gray-900"
              >
                <HiOutlineArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{decodeURIComponent(streamName)}</h1>
              <p className="text-xs text-gray-500">{logGroupName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <HiRefresh className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin [animation-direction:reverse]" : ""}`} />
            Actualiser
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
            <div className="relative min-w-[180px] flex-1">
              <HiOutlineSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-9 pr-8 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              {search && (
                <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <HiOutlineX className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative min-w-[160px]">
              <input
                type="text"
                placeholder="Admission ID"
                value={admissionIdFilter}
                onChange={(e) => setAdmissionIdFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-3 pr-8 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              {admissionIdFilter && (
                <button type="button" onClick={() => setAdmissionIdFilter("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <HiOutlineX className="h-4 w-4" />
                </button>
              )}
            </div>

            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">Tous ({counts.all})</option>
              <option value="error">Erreurs ({counts.error})</option>
              <option value="warn">Alertes ({counts.warn})</option>
              <option value="success">Succès ({counts.success})</option>
              <option value="info">Info ({counts.info})</option>
            </select>

            <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onPreset(p.key)}
                  className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${preset === p.key ? "bg-white text-primary-500 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDatePicker((v) => !v)}
                className={`inline-flex items-center rounded-lg border px-2.5 py-1.5 text-sm ${customStart ? "border-primary-500 bg-primary-50 text-primary-500" : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
              >
                <HiOutlineCalendar className="h-4 w-4" />
              </button>

              {showDatePicker && (
                <div className="absolute right-0 top-full z-50 mt-1 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
                  <label className="mb-1 block text-xs text-gray-500">Début</label>
                  <DateTimeInput
                    value={customStart}
                    onChange={(dateTime) => {
                      setCustomStart(dateTime);
                      setPreset(null);
                    }}
                    className="mb-2 w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                  <label className="mb-1 block text-xs text-gray-500">Fin</label>
                  <DateTimeInput
                    value={customEnd}
                    onChange={(dateTime) => {
                      setCustomEnd(dateTime);
                      setPreset(null);
                    }}
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>

            <span className="ml-auto text-xs text-gray-500">{filteredLogs.length.toLocaleString()} résultats</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center text-gray-400">Impossible de charger les logs</div>
          ) : filteredLogs.length === 0 ? (
            <div className="py-16 text-center text-gray-400">{logs.length === 0 ? "Aucun log sur cette période" : "Aucun log trouvé"}</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredLogs.map((log) => {
                const parsed = log.parsed;
                const level = getLogLevel(parsed);
                const key = log.key;
                const isExpanded = expandedKeys[key];

                return (
                  <div key={key} className={`border-l-2 ${levelAccent(level)}`}>
                    <div className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50" onClick={() => toggleExpand(key)}>
                      <span className="text-gray-300">{isExpanded ? <HiOutlineChevronDown className="h-4 w-4" /> : <HiOutlineChevronRight className="h-4 w-4" />}</span>
                      <span className="flex-shrink-0">{levelIcon(level)}</span>
                      <span className="flex-shrink-0 font-mono text-xs text-gray-400">{formatTime(parsed.timestamp || log.timestamp)}</span>
                      {parsed.service && <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{parsed.service}</span>}
                      {parsed.webAppUsername && <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{parsed.webAppUsername}</span>}
                      {parsed.laboratoryCode && <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">Lab {parsed.laboratoryCode}</span>}
                      <span className="flex-1 truncate text-gray-700">{parsed.message}</span>
                    </div>

                    {isExpanded && (
                      <div className="px-3 pb-3 pl-9">
                        {parsed.isParsed && (
                          <div className="mb-2 grid grid-cols-2 gap-2">
                            {parsed.admissionId && (
                              <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                                <p className="mb-0.5 text-[10px] font-medium text-gray-500">Admission ID</p>
                                <p className="truncate font-mono text-xs text-gray-800" title={parsed.admissionId}>
                                  {parsed.admissionId}
                                </p>
                              </div>
                            )}
                            {parsed.messageId && (
                              <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                                <p className="mb-0.5 text-[10px] font-medium text-gray-500">Message ID</p>
                                <p className="truncate font-mono text-xs text-gray-800" title={parsed.messageId}>
                                  {parsed.messageId}
                                </p>
                              </div>
                            )}
                            {parsed.timestamp && (
                              <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                                <p className="mb-0.5 text-[10px] font-medium text-gray-500">Timestamp</p>
                                <p className="font-mono text-xs text-gray-800">{formatTimestamp(parsed.timestamp)}</p>
                              </div>
                            )}
                            {parsed.caller && (
                              <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                                <p className="mb-0.5 text-[10px] font-medium text-gray-500">Caller</p>
                                <p className="truncate font-mono text-xs text-gray-800" title={`${parsed.caller.file}:${parsed.caller.line}`}>
                                  {parsed.caller.file?.split(/[/\\]/).pop()}:{parsed.caller.line}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="group relative rounded-lg bg-gray-900 p-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(parsed.isParsed ? JSON.stringify(parsed.raw, null, 2) : log.message);
                            }}
                            className="absolute right-2 top-2 rounded bg-gray-700 p-1 text-gray-400 opacity-0 transition-colors hover:bg-gray-600 hover:text-white group-hover:opacity-100"
                          >
                            <HiOutlineClipboardCopy className="h-3 w-3" />
                          </button>
                          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-gray-300">
                            {parsed.isParsed ? JSON.stringify(parsed.raw, null, 2) : log.message}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {hasNextPage && (
            <div className="border-t border-gray-100 p-3 text-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                {isFetchingNextPage ? "Chargement..." : "Charger plus"}
              </button>
            </div>
          )}
        </div>
      </div>

      {showDatePicker && <div className="fixed inset-0 z-40" onClick={() => setShowDatePicker(false)} />}
    </div>
  );
};

export default MachineLogs;
